import * as React from "react";

import { cn } from "@/lib/cn";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

/**
 * Defaults disable every browser-supplied "helpful" behaviour on text
 * inputs: autocomplete suggestions, autocorrect, autocapitalize, and
 * spell-check. This is an admin/integration tool — entries are
 * identifiers, paths, JSONata, UUIDs, etc., where suggestions are
 * actively wrong.
 *
 * Callers can override per-instance — e.g. LoginPage sets
 * `autoComplete="username"` / `"current-password"` so password
 * managers still work. The `{...props}` spread ensures explicit
 * values win over the defaults below.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      // A random-ish name confuses Chrome/Safari's heuristic that
      // ignores autoComplete="off" on inputs that look like login
      // fields. Only applies when the caller hasn't set a name.
      data-1p-ignore
      data-lpignore="true"
      className={cn(
        "flex h-8 w-full rounded-md border border-border bg-surface px-2.5 text-sm",
        "text-foreground placeholder:text-muted-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";
